import { useQueries } from '@tanstack/react-query'
import { Contact } from 'lucide-react'
import React, { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import {
  getBrokersMembers,
  getGetBrokersMembersQueryKey,
  useGetBrokersMembers,
} from 'utils/web_api'

import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from '../../reusable/table/ComboboxMultiSelectFilter'
import { type MultipleSelectedValuesFilter } from '../types'
import FilterManagementDropdown from './FilterManagementDropdown'

const pagination = {
  'page[number]': 1,
  'page[size]': 10,
}

const MemberFilter = (props: MultipleSelectedValuesFilter) => {
  const [query, setQuery] = useState('')
  // Debounce the query to a request is only sent after the user stops typing for 300ms.
  const [queryDebounced] = useDebounceValue(query, 300)

  const { data, isLoading } = useGetBrokersMembers({
    ...pagination,
    'filter[name]': queryDebounced || undefined,
  })
  const members = data?.data || []

  const memberOptions: ComboboxFilterOption<string>[] =
    members
      ?.filter((member) => member.id && member.name)
      .map(({ id, name }) => {
        return {
          label: name!,
          value: id!,
        }
      }) || []

  // If the selected members are not in the results, fetch
  // the missing members.
  const membersMissingFromResults = props.selectedValues.filter(
    (id) => !memberOptions.some(({ value }) => value === id)
  )

  const missingmemberResults = useQueries({
    queries: membersMissingFromResults.map((id) => ({
      queryKey: getGetBrokersMembersQueryKey({
        ...pagination,
        'filter[id]': id,
      }),
      queryFn: () =>
        getBrokersMembers({
          ...pagination,
          'filter[id]': id,
        }),
    })),
  })

  // Merge the results so that the selected members are included in the options.
  const memberOptionsIncludingSelectedmembers = [
    ...memberOptions,
    ...(missingmemberResults
      ?.map(({ data }) => data?.data?.[0])
      .filter((member) => member?.id && member?.name)
      .map((member) => ({
        label: member!.name!,
        value: member!.id!,
      })) || []),
  ]

  return (
    <div>
      <ComboboxMultiSelectFilter
        {...props}
        options={[
          {
            groupLabel: 'Members',
            options: memberOptionsIncludingSelectedmembers,
          },
        ]}
        placeholder='Search members to filter...'
        onValueChange={setQuery}
        isLoading={isLoading}
      >
        <FilterManagementDropdown
          {...props}
          options={memberOptionsIncludingSelectedmembers}
          noSelectionText='Member'
          pluralIdentifier='members'
        >
          <Contact size={20} />
        </FilterManagementDropdown>
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default MemberFilter
